// ADR-110: My preferences — every person (owner, Seller, Storekeeper) picks their own language and theme. Each
// choice applies at once and is kept on this device; the business language stays the default and the language of
// documents.
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BottomSheet, Button, Chips, Notice, Section, SheetActions } from '../design/kit';
import { applyLanguage, businessLanguage, languages, type Language } from '../i18n';
import { applyTheme, savedTheme, setUserLanguage, themes, userLanguage, type Theme } from '../lib/ux/preferences';
import { useSession } from './session';

const LANGUAGE_NAMES: Record<Language, string> = { ar: 'العربية', fr: 'Français', en: 'English' };

export function PreferencesSheet({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const { userId } = useSession();
  const [lang, setLang] = useState<Language | 'business'>(userLanguage(userId) ?? 'business');
  const [theme, setTheme] = useState<Theme>(savedTheme());
  const business = businessLanguage();

  const chooseLanguage = (id: string) => {
    const next = id === 'business' ? null : (id as Language);
    setUserLanguage(userId, next);
    setLang(next ?? 'business');
    void applyLanguage(next ?? business);
  };
  const chooseTheme = (id: string) => {
    applyTheme(id as Theme);
    setTheme(id as Theme);
  };

  return (
    <BottomSheet title={t('pref.title')} sub={t('pref.sub')} open onClose={onClose}>
      <Section title={t('pref.language')}>
        <Chips
          label={t('pref.language')}
          items={[
            { id: 'business', label: t('pref.business', { lang: LANGUAGE_NAMES[business] }) },
            ...languages.map((l) => ({ id: l, label: LANGUAGE_NAMES[l] })),
          ]}
          selected={[lang]}
          onToggle={chooseLanguage}
        />
      </Section>
      <Section title={t('pref.theme')}>
        <Chips
          label={t('pref.theme')}
          items={themes.map((th) => ({ id: th, label: t(`pref.theme.${th}`) }))}
          selected={[theme]}
          onToggle={chooseTheme}
        />
      </Section>
      <Notice tone="info">{t('pref.note')}</Notice>
      <SheetActions>
        <Button variant="main" onClick={onClose}>
          {t('pref.done')}
        </Button>
      </SheetActions>
    </BottomSheet>
  );
}
